import { useMutation, type UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getMenuForUpdate,
  getMenuLock,
  getMenuPermissions,
  hydrateMenuDataWithPermissions,
  MenuApiKeysEnum,
  updateMainMenu,
} from '~services/api/calls/menu';
import { ApiErrorDescriptor } from '~services/api/helpers';

export const useMenuLock = (
  options?: UseQueryOptionsBase<
    {
      status: string;
      info: string;
    },
    ApiErrorDescriptor
  >,
) =>
  useQuery({
    queryKey: [MenuApiKeysEnum.GetMenuLock],
    queryFn: async () => await getMenuLock(),
    refetchInterval: 5000, // Will refetch the lock at given interval in ms
    retry: 3, // Will retry failed requests 3 times before displaying an error
    ...options,
  });

export const useMenuPermissions = (options?: UseQueryOptionsBase<MenuPermissionsDto, ApiErrorDescriptor>) =>
  useQuery({
    queryKey: [MenuApiKeysEnum.GetMenuPermissions],
    queryFn: async () => await getMenuPermissions(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    placeholderData: [],
    ...options,
  });

export const useEditmodeMenu = (options?: UseQueryOptionsBase<MenuElement[], ApiErrorDescriptor>) => {
  const { data: responsePermissions, isFetched } = useMenuPermissions();
  const q = useQuery({
    queryKey: [MenuApiKeysEnum.GetMenuForUpdate, responsePermissions],
    queryFn: async () => {
      const responseMenu = await getMenuForUpdate();
      return hydrateMenuDataWithPermissions(responseMenu, responsePermissions);
    },
    refetchOnWindowFocus: false,
    enabled: isFetched,
    ...options,
  });
  return { ...q, isLoading: !isFetched || q.isLoading };
};

export const useUpdateMenu = (
  options?: UseMutationOptions<
    {
      status?: string;
      info?: string;
    },
    ApiErrorDescriptor,
    MenuElementChange[]
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMainMenu,
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: [MenuApiKeysEnum.GetMenuForUpdate] });
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: [MenuApiKeysEnum.GetMenuForUpdate] });
      if (options.onError) {
        void options.onError(error, variables, context);
      }
    },
  });
};
